/**
 * PrmomtionListingResults to show suggestion of what to show after something is bought or added to cart.
 *
 * @module views/components/PromotionListingResults
 * @memberof -Common
 */
import './PromotionListingResults.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import constants from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import CompactDealCard from '@ulta/components/CompactDealCard/CompactDealCard';
import ProductListingWrapper from '@ulta/components/ProductListingWrapper/ProductListingWrapper';

/**
 * Represents a PromotionListingResults component
 *
 * @method
 * @param {PromotionListingResultsProps} props - React properties passed from composition
 * @returns PromotionListingResults
 */
export const PromotionListingResults = function( props ){
  return (
    <ProductListingWrapper
      { ...spreadProps( { ...props } ) }
      productsViewedLabel={ props.promotionsViewedLabel }
      productsViewedTemplateLabel={ props.promotionsViewedLabelTemplate }
      itemSelectorTemplate='[data-promotion-id="{id}"] .pal-c-Link'
      itemSelectorClassName='PromotionListingResults__compactDealCard'
      itemSelectorAttribute='data-promotion-id'
      itemIndexAttribute='promotionId'
      className='PromotionListingResults'
    >
      { ( { renderChildren, onIntersectionEvent } ) => (
        <ul className='PromotionListingResults__promotionList'
          data-test='promotion-list'
        >
          { renderChildren( ( product, index ) => (
            <li className={ classNames( 'PromotionListingResults__compactDealCard', {
              'PromotionListingResults__compactDealCardGWP': !props.isGrid
            } ) }
            data-test='products-list-item'
            key={ `${product.headline}_${index}` }
            data-promotion-id={ product.promotionId }
            >
              <CompactDealCard
                images={ product.images }
                eyebrow={ product.eyebrow }
                headline={ product.headline }
                subHeadline={ product.subHeadline }
                tag={ product.tag }
                itemClickAction={ product.itemClickAction }
                isGrid={ props.isGrid }
                onIntersectionEvent={ onIntersectionEvent }
                promotionId={ product.promotionId }
              />
            </li>
          ) ) }
        </ul>
      ) }
    </ProductListingWrapper>
  );
};

/**
 * Property type definitions
 *
 * @typedef PromotionListingResultsProps
 * @type {object}
 * @property {array} items - Sets the items of the page
 * @property {boolean} loading - Flag to render the data or not
 * @property {object} filtersColumn - Sets the filters column for the page
 * @property {string} resultCountLabel - Sets the result count label for the page
 * @property {string} noResultsLabel - Sets the no result label for the page
 * @property {object} loadMoreAction - Sets the loadMoreAction for the page
 * @property {object} loadMoreUrl - Sets the load more client side url
 * @property {string} promotionsViewedLabel - Sets the promotionsViewedLabel for the page
 * @property {string} promotionsViewedLabelTemplate - Sets the promotionsViewedLabelTemplate for the page
 * @property {string} noFilteredResultsLabel - Sets the noFilteredResultsLabel for the page
 * @property {object} resetFiltersAction - Sets the resultFIltersAction for the page
 * @property {string} resetFiltersUrl - Sets the url for after a filter reset
 * @property {string} currentUrl - Sets the current url for page
 * @property {boolean} isGrid - Flag to render BMSM or GWP
 */
export const propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  filtersColumn: PropTypes.object,
  resultCountLabel: PropTypes.string,
  noResultsLabel: PropTypes.string,
  loadMoreAction: PropTypes.object,
  loadMoreUrl: PropTypes.string,
  promotionsViewedLabel: PropTypes.string,
  promotionsViewedLabelTemplate: PropTypes.string,
  noFilteredResultsLabel: PropTypes.string,
  resetFiltersAction: PropTypes.object,
  resetFiltersUrl: PropTypes.string,
  currentUrl: PropTypes.string,
  isGrid: PropTypes.bool
};

export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};
PromotionListingResults.propTypes = propTypes;
PromotionListingResults.defaultProps = defaultProps;

export default PromotionListingResults;
